import { useMemo } from 'react'
import { Plus, Trash2, Undo2 } from 'lucide-react'
import { useTerminology } from '@/lib/useTerminology'
import { kennzeile } from '../project-labels'
import type { PositionRow } from '../types/phase-d.types'

/**
 * AP-35 Nachtrag 7 — **der Rahmen rechts im Positionskopf**, seit Nachtrag 15 (N15-6) gekürzt: Suchfeld,
 * Blättern ‹ › und der Zähler stehen jetzt an der Überschrift der Position (`components/positionswahl`),
 * weil die Positionsliste links entfallen ist — zweimal dasselbe im selben Kopf wäre ein Rätsel.
 *
 * Geblieben ist eine Zeile: ↶ zur zuletzt geöffneten Position — und die Aktionen der Position:
 * Freigeben, Löschen, Neu.
 */
export function PositionNavigator({
  aktuell,
  alle,
  zuletzt,
  onOeffne,
  onNeu,
  onFreigeben,
  onLoeschen,
  laeuft,
}: {
  aktuell: PositionRow
  /** Alle Positionen des Projekts — für die Bezeichnung der zuletzt geöffneten. */
  alle: readonly PositionRow[]
  /** Zuletzt geöffnete Positionen dieses Projekts, jüngste zuerst. */
  zuletzt: readonly string[]
  onOeffne: (id: string) => void
  onNeu?: () => void
  onFreigeben?: () => void
  onLoeschen?: () => void
  laeuft?: boolean
}) {
  const terms = useTerminology()
  const nachId = useMemo(() => new Map(alle.map((p) => [p.id, p])), [alle])
  const zurueckZu = zuletzt.map((id) => nachId.get(id)).find((p): p is PositionRow => !!p && p.id !== aktuell.id) ?? null
  const knopfKlein =
    'inline-flex h-[26px] w-[26px] items-center justify-center border border-border bg-background text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-40 disabled:hover:bg-background'

  return (
    <div className="flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-background p-1">
      <button
        type="button"
        className={`${knopfKlein} rounded-md`}
        onClick={() => zurueckZu && onOeffne(zurueckZu.id)}
        disabled={!zurueckZu}
        title={zurueckZu ? `Zurück zu Pos. ${zurueckZu.number}: ${kennzeile(zurueckZu)}` : 'Noch nichts anderes geöffnet'}
        aria-label="Zuletzt geöffnet"
      >
        <Undo2 className="h-4 w-4" />
      </button>
      {onFreigeben && (
        <button type="button" disabled={laeuft} className="inline-flex h-[26px] items-center rounded-md bg-primary px-2.5 text-sm text-primary-foreground disabled:opacity-50" onClick={onFreigeben}>
          Freigeben
        </button>
      )}
      {onLoeschen && (
        <button
          type="button"
          disabled={laeuft}
          className={`${knopfKlein} rounded-md text-destructive`}
          onClick={onLoeschen}
          title={`${terms.position} löschen — 30 Tage im Papierkorb`}
          aria-label={`${terms.position} löschen`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
      {onNeu && (
        <button type="button" className="inline-flex h-[26px] items-center gap-1 rounded-md border border-border px-2.5 text-sm" onClick={onNeu}>
          <Plus className="h-4 w-4" /> Neu
        </button>
      )}
    </div>
  )
}
